import { EditionMode } from '../store/types.js';
import type { RecipeStore } from '../store/types.js';

/**
 * Shared hover/click wiring for IngredientsView/PreparationView — both
 * render a `[data-node-id]` element per row (`.ingredient-row` /
 * `.preparation-step-row`), and PreparationView additionally embeds inline
 * `.preparation-ingredient` spans (each with its own `data-node-id`) inside
 * a step's rendered text for every node it references — same markup
 * convention NodeVisualizer's own step-text rendering uses for its
 * `selectAll('[data-node-id]')` wiring. Wiring every `[data-node-id]`
 * element rather than just the outer row picks up those inline references
 * too, so a specific ingredient mentioned inside a step is its own
 * hover/click target, not just the step as a whole.
 *
 * - Hover sets/clears the store's hovered node. No callback into
 *   GraphVisualizer needed — GraphVisualizer's own reactive re-render
 *   already highlights the corresponding node on the canvas from
 *   `store.isNodeHovered()`, same as any other store change.
 * - Click reveals the node (unfolds any folded ancestors — a pure store
 *   operation, `store.revealNode()`) then selects it
 *   (`store.selectNodeWithAutoRecipe()`). Both go straight to the store;
 *   see docs/wiki/frontend.md for why this doesn't need a GraphVisualizer
 *   callback the way ListView's *mutating* actions do — these nodes always
 *   belong to the already-selected recipe, so there's no cross-recipe
 *   unsaved-changes case to guard, and GraphVisualizer's own reactive
 *   subscription already re-renders everything these calls need.
 * - Exception: while picking a node for a transformation parameter
 *   (store.getEditionSubMode() === PICK_NODE), a click completes the pick
 *   instead — same as ListView's row click — via the caller-supplied
 *   onCompletePickNode, since finishing a pick is GraphVisualizer-owned
 *   state (see GraphVisualizer.completePickNode()).
 *
 * Attached directly to each element (not delegated) since rows are
 * recreated on every render anyway.
 */
export function wirePanelRowEvents(
  container: HTMLElement,
  store: RecipeStore,
  onCompletePickNode?: (nodeId: string) => void,
): void {
  const elements = container.querySelectorAll('[data-node-id]');
  for (const element of Array.from(elements)) {
    const el = element as HTMLElement;
    const nodeId = el.getAttribute('data-node-id');
    if (!nodeId) continue;

    el.addEventListener('mouseenter', () => {
      store.setHoveredNode(nodeId);
    });
    el.addEventListener('mouseleave', () => {
      store.setHoveredNode(null);
    });
    el.addEventListener('click', (e) => {
      if (store.getEditionSubMode() === EditionMode.PICK_NODE) {
        e.stopPropagation();
        onCompletePickNode?.(nodeId);
        return;
      }
      store.revealNode(nodeId).then(() => {
        store.selectNodeWithAutoRecipe(nodeId);
      });
    });
  }
}
